import { ChangeEvent, ElementType, useCallback, useState } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Link from '@mui/material/Link'
import { styled } from '@mui/material'
import Auth from '../../../lib/commons/auth'
import AlertDialog from '../../components/dialogs/AlertDialog'
import TextInput from '../../components/editors/input/TextInput'
import store from '../../../store'
import { addScreenMessage, MessageLevel } from '../../../store/messages'
import { DialogState } from './commons/LoginContainer'
import { intl } from '../../../i18n'
import { usePasswordField } from '../../hooks/usePasswordField'
import { usePasswordVisibility } from '../../hooks/usePasswordVisibility'

import { FontSize } from '../../../styles/commonStyles'
import { colorPalette } from '../../style/colorPalette'
import { PasswordVisibilityAdornment } from '../../components/inputs/PasswordVisibilityAdornment'
import { NoticeText } from '../../components/labels/NoticeText'
import { whitespaceNoticeViewOf } from '../../../utils/password'

const NewPasswordForConfirmInput = styled(TextInput)({
  marginTop: 0,
  marginBottom: '8px',
})
const OldPasswordHelperText = styled('span')({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'flex-start',
  gap: '8px',
})
const ForgotPasswordLink = styled(Link)<{ component?: ElementType }>({
  flexShrink: 0,
  fontSize: FontSize.MEDIUM,
  color: colorPalette.skyBlue[8],
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  fontFamily: 'inherit',
  background: 'none',
  border: 'none',
  padding: 0,
})

const ChangePassword = (props: RouteComponentProps) => {
  const [oldPassword, setOldPassword] = useState<string>('')
  // 空白入りのパスワードを使っている既存ユーザーが変更手段を失わないよう、
  // 現在のパスワード欄には空白の除去・入力抑止を適用しない
  const oldPasswordVisibility = usePasswordVisibility()
  const newPassword = usePasswordField()
  const newPasswordForConfirm = usePasswordField()
  const [dialog, setDialog] = useState<DialogState>({
    open: false,
    message: '',
  })

  const onChangeOldPassword = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setOldPassword(event.target.value)
    },
    []
  )

  const close = useCallback(() => {
    props.history.goBack()
  }, [props.history])

  const toForgotPassword = useCallback(async () => {
    // サインアウトに失敗しても再設定自体は続行できるため、画面遷移は必ず行う
    try {
      await Auth.getCurrentTenant()?.logout()
    } catch (err) {
      console.warn('Failed to sign out before moving to forgot password.', err)
    }
    props.history.push('/forgotPassword')
  }, [props.history])

  const openAlertDialog = useCallback((message: string) => {
    setDialog({ open: true, message })
  }, [])

  const closeAlertDialog = useCallback(() => {
    setDialog({ open: false, message: '' })
  }, [])

  const onSubmit = useCallback(async () => {
    // Check required item.
    const errorMessages: string[] = []
    if (!oldPassword) {
      errorMessages.push(
        intl.formatMessage({
          id: 'changePassword.enterOldPassword',
        })
      )
    }
    if (!newPassword.value) {
      errorMessages.push(
        intl.formatMessage({
          id: 'changePassword.enterNewPassword',
        })
      )
    }
    if (!newPasswordForConfirm.value) {
      errorMessages.push(
        intl.formatMessage({
          id: 'changePassword.enterNewPasswordCheck',
        })
      )
    }
    if (errorMessages.length > 0) {
      openAlertDialog(errorMessages.join('\n'))
      return
    }

    // Check if password for confirm is equal to password.
    if (newPassword.value !== newPasswordForConfirm.value) {
      openAlertDialog(
        intl.formatMessage({
          id: 'changePassword.passwordNotMatch',
        })
      )
      return
    }

    await Auth.changePassword(oldPassword, newPassword.value)
      .then(() => {
        store.dispatch(
          addScreenMessage(store.getState().appFunction.currentExternalId!, {
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({
              id: 'changePassword.succeeded',
            }),
          })
        )
        close()
      })
      .catch(err => {
        openAlertDialog(err.message)
      })
  }, [
    oldPassword,
    newPassword.value,
    newPasswordForConfirm.value,
    openAlertDialog,
    close,
  ])

  return (
    <Dialog
      open={true}
      aria-labelledby="changePasswordDialogTitle"
      aria-describedby="changePasswordDialogDescription"
    >
      <AlertDialog
        isOpen={dialog.open}
        message={dialog.message}
        closeButtonTitle="OK"
        closeHandler={closeAlertDialog}
      />
      <DialogTitle id="changePasswordDialogTitle">
        {intl.formatMessage({ id: 'changePassword' })}
      </DialogTitle>
      <DialogContent>
        <TextInput
          variant="outlined"
          margin="normal"
          required={true}
          fullWidth={true}
          id="oldPassword"
          label={intl.formatMessage({
            id: 'changePassword.oldPassword',
          })}
          name="oldPassword"
          type={oldPasswordVisibility.inputType}
          autoFocus={true}
          value={oldPassword}
          onChange={onChangeOldPassword}
          InputProps={{
            endAdornment: (
              <PasswordVisibilityAdornment
                visible={oldPasswordVisibility.showPassword}
                onToggle={oldPasswordVisibility.toggleShowPassword}
              />
            ),
          }}
          helperText={
            <OldPasswordHelperText>
              <ForgotPasswordLink
                component="button"
                type="button"
                onClick={toForgotPassword}
              >
                {intl.formatMessage({ id: 'login.forgotPassword' })}
              </ForgotPasswordLink>
            </OldPasswordHelperText>
          }
        />
        <TextInput
          variant="outlined"
          margin="normal"
          required={true}
          fullWidth={true}
          id="newPassword"
          label={intl.formatMessage({
            id: 'changePassword.newPassword',
          })}
          name="newPassword"
          type={newPassword.inputType}
          autoFill="new-password"
          value={newPassword.value}
          onChange={newPassword.onChange}
          onKeyDown={newPassword.onKeyDown}
          InputProps={{
            endAdornment: (
              <PasswordVisibilityAdornment
                visible={newPassword.showPassword}
                onToggle={newPassword.toggleShowPassword}
              />
            ),
          }}
          helperText={
            <NoticeText
              {...whitespaceNoticeViewOf(newPassword.whitespaceNotice)}
            />
          }
        />
        <NewPasswordForConfirmInput
          variant="outlined"
          required={true}
          fullWidth={true}
          id="newPasswordForConfirm"
          label={intl.formatMessage({
            id: 'changePassword.newPasswordForConfirm',
          })}
          name="newPasswordForConfirm"
          type={newPasswordForConfirm.inputType}
          value={newPasswordForConfirm.value}
          onChange={newPasswordForConfirm.onChange}
          onKeyDown={newPasswordForConfirm.onKeyDown}
          InputProps={{
            endAdornment: (
              <PasswordVisibilityAdornment
                visible={newPasswordForConfirm.showPassword}
                onToggle={newPasswordForConfirm.toggleShowPassword}
              />
            ),
          }}
          helperText={
            <NoticeText
              {...whitespaceNoticeViewOf(
                newPasswordForConfirm.whitespaceNotice
              )}
            />
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>
          {intl.formatMessage({ id: 'changePassword.close' })}
        </Button>
        <Button onClick={onSubmit} color="secondary">
          {intl.formatMessage({ id: 'changePassword.edit' })}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default withRouter(ChangePassword)
